// Pure helpers for the Inventory scroll-to-collapse focus mode. Extracted so the
// two-ref state machine (`focusChangedAt` / `lastUnfocusIntentAt`) and the
// wheel-direction-aware body scroll heuristic can be unit-tested without a DOM.

interface IViewportMetrics {
  clientHeight: number;
  scrollHeight: number;
  scrollTop: number;
}

// Returns true when the focus lock is engaged — i.e., the caller should ignore
// the unfocus signal because the collapse transition is still settling and the
// layout-induced scroll rebound (AG-Grid clamping `scrollTop` to 0 after the
// grid grows into the freed space) would otherwise re-fire the unfocus branch.
//
// The lock is bypassed when the user has signaled real unfocus intent since the
// focus change — an upward wheel that post-dates `focusChangedAt`. Without such
// intent the rebound is presumed and the unfocus is suppressed.
export const isFocusLocked = (
  focusChangedAt: number,
  lastUnfocusIntentAt: number,
  now: number,
  lockMs: number
) => {
  if (now - focusChangedAt >= lockMs) {
    return false;
  }

  return lastUnfocusIntentAt <= focusChangedAt;
};

// Returns true when a wheel event with the given `deltaY` would actually scroll
// the AG-Grid body viewport in the wheel direction. Used to gate deferral to
// AG-Grid's `onBodyScroll`: defer only when the body is genuinely about to
// scroll, otherwise fall through to the panel-level handler.
//
// "Body has scrollable overflow at all" is too coarse — a body at the top with
// the user wheeling up (or at the bottom and wheeling down) doesn't scroll, so
// `onBodyScroll` never fires and the wheel is swallowed.
//
// `tolerancePx` (default 1) absorbs sub-pixel rounding on fractional
// devicePixelRatio displays (macOS retina at 1.5×/1.75× zoom).
export const willBodyScroll = (
  viewport: IViewportMetrics | null | undefined,
  deltaY: number,
  tolerancePx = 1
) => {
  if (!viewport) {
    return false;
  }

  if (deltaY > 0) {
    return viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop > tolerancePx;
  }

  if (deltaY < 0) {
    return viewport.scrollTop > tolerancePx;
  }

  return false;
};
